/*
  Pack quantity guard
  ───────────────────
  Pack variants ("Pack of 2", "Pack of 3" in an option named Pack/Bundle)
  are priced PER UNIT in admin, so a pack must sit in the cart with a
  quantity that is a multiple of its size (Pack of 3 → 3, 6, 9 …).

  1. Cart guard: after every cart add/change/update made through fetch
     (theme, Shiprocket Smart Cart, quick-add, Buy Now …) the cart is
     checked and any pack line with a wrong quantity is corrected:
       • quantity went down  → round down to whole packs (0 = remove)
       • otherwise           → round up to whole packs
     For change/update calls the caller receives the corrected cart, so
     the drawer re-renders with the right quantity.

  2. Shiprocket drawer: each line shows its line total (unit × qty)
     instead of the unit price, so it matches the Estimated Total.

  Loaded early in <head> (layout/theme.liquid) so fetch is wrapped
  before app scripts start using it.
*/
(function () {
  'use strict';
  if (window.__packQtyGuard || !window.fetch) return;
  window.__packQtyGuard = true;

  var nativeFetch = window.fetch.bind(window);
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var CART_JS = root + 'cart.js';
  var CHANGE_JS = root + 'cart/change.js';

  var state = null;   /* last cart JSON seen */
  var known = {};     /* line key → quantity before the latest change */
  var queue = Promise.resolve();

  /* ── helpers ─────────────────────────────────────────── */
  function packSize(item) {
    var opts = item.options_with_values || [];
    for (var i = 0; i < opts.length; i++) {
      var name = String(opts[i].name || '').toLowerCase();
      if (name.indexOf('pack') !== -1 || name.indexOf('bundle') !== -1) {
        var m = String(opts[i].value || '').match(/\d+/);
        var n = m ? parseInt(m[0], 10) : 1;
        return n > 1 ? n : 1;
      }
    }
    return 1;
  }

  function urlOf(input) {
    try {
      return typeof input === 'string' ? input : (input && input.url) || '';
    } catch (e) { return ''; }
  }

  function isCartWrite(url) {
    return /\/cart\/(add|change|update)(\.js)?(\?|$)/.test(url);
  }
  function isCartRead(url) {
    return /\/cart(\.js)?(\?|$)/.test(url) && !/\/cart\/(add|change|update|clear)/.test(url);
  }

  /* pull `sections` / `sections_url` out of the caller's request body */
  function sectionParams(body) {
    var out = {};
    if (!body) return out;
    try {
      if (typeof body === 'string') {
        if (body.charAt(0) === '{') {
          var j = JSON.parse(body);
          if (j.sections) out.sections = j.sections;
          if (j.sections_url) out.sections_url = j.sections_url;
        } else {
          var p = new URLSearchParams(body);
          if (p.get('sections')) out.sections = p.get('sections');
          if (p.get('sections_url')) out.sections_url = p.get('sections_url');
        }
      } else if (typeof FormData !== 'undefined' && body instanceof FormData) {
        if (body.get('sections')) out.sections = body.get('sections');
        if (body.get('sections_url')) out.sections_url = body.get('sections_url');
      } else if (body instanceof URLSearchParams) {
        if (body.get('sections')) out.sections = body.get('sections');
        if (body.get('sections_url')) out.sections_url = body.get('sections_url');
      }
    } catch (e) { /* ignore */ }
    return out;
  }

  function remember(cart) {
    if (!cart || !cart.items) return;
    state = cart;
    known = {};
    cart.items.forEach(function (it) { known[it.key] = it.quantity; });
    paintSoon();
  }

  function getCart() {
    return nativeFetch(CART_JS, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  /* correct every pack line; resolves with the last /cart/change Response (or null) */
  function fixCart(extra) {
    var before = known;
    return getCart().then(function (cart) {
      var fixes = [];
      (cart.items || []).forEach(function (it) {
        var n = packSize(it);
        if (n <= 1 || it.quantity % n === 0) return;
        var prev = before[it.key];
        var target = (prev != null && it.quantity < prev)
          ? Math.floor(it.quantity / n) * n
          : Math.ceil(it.quantity / n) * n;
        fixes.push({ id: it.key, quantity: target });
      });

      if (!fixes.length) { remember(cart); return null; }

      var last = null;
      return fixes.reduce(function (p, fix, idx) {
        return p.then(function () {
          var body = { id: fix.id, quantity: fix.quantity };
          if (idx === fixes.length - 1 && extra) {
            if (extra.sections) body.sections = extra.sections;
            if (extra.sections_url) body.sections_url = extra.sections_url;
          }
          return nativeFetch(CHANGE_JS, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body)
          }).then(function (r) { last = r; });
        });
      }, Promise.resolve()).then(function () {
        return last.clone().json().then(function (c) { remember(c); return last; });
      });
    });
  }

  function run(task) {
    var p = queue.then(task, task);
    queue = p.catch(function () {});
    return p;
  }

  /* ── fetch wrapper ───────────────────────────────────── */
  window.fetch = function (input, init) {
    var url = urlOf(input);
    var method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();

    if (isCartWrite(url) && method === 'POST') {
      var extra = sectionParams(init && init.body);
      var isAdd = /\/cart\/add/.test(url);
      return nativeFetch(input, init).then(function (res) {
        if (!res.ok) return res;
        return run(function () { return fixCart(extra); }).then(function (fixed) {
          /* change/update callers render from the returned cart → hand
             them the corrected one. add callers get their item JSON. */
          if (fixed && !isAdd) return fixed;
          return res;
        }, function () { return res; });
      });
    }

    if (isCartRead(url) && method === 'GET' && /\.js(\?|$)|\.json/.test(url)) {
      return nativeFetch(input, init).then(function (res) {
        if (res.ok) res.clone().json().then(function (c) { if (c && c.items) { state = c; paintSoon(); } }).catch(function () {});
        return res;
      });
    }

    return nativeFetch(input, init);
  };

  /* fix anything already wrong in the cart (old carts, XHR adds) */
  function initialCheck() { run(function () { return fixCart(null); }).catch(function () {}); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialCheck);
  else initialCheck();
  document.addEventListener('visibilitychange', function () { if (!document.hidden) initialCheck(); });

  /* ── Shiprocket drawer: unit price → line total ──────── */
  var PRICE_RE = /^\s*₹\s?([\d,]+(?:\.\d+)?)\s*$/;
  var unitOf = new WeakMap();   /* text node → { cents, decimals } (original unit price) */
  var observed = null;
  var scheduled = false;

  function fmt(cents, decimals) {
    var v = cents / 100;
    return '₹' + (decimals
      ? v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : v.toLocaleString('en-IN', { maximumFractionDigits: 2 }));
  }

  function textNodes(scope) {
    var out = [];
    var w = document.createTreeWalker(scope, NodeFilter.SHOW_TEXT, null);
    var n;
    while ((n = w.nextNode())) { if (n.nodeValue && n.nodeValue.trim()) out.push(n); }
    return out;
  }

  function norm(s) { return String(s || '').replace(/\s+/g, ' ').replace(/[–—]/g, '-').trim().toLowerCase(); }

  function paint() {
    scheduled = false;
    var host = document.getElementById('shiprocket-smart-cart-root');
    var sr = host && host.shadowRoot;
    if (!sr || !state || !state.items) return;

    if (observed !== sr) {
      observed = sr;
      new MutationObserver(paintSoon).observe(sr, { childList: true, subtree: true, characterData: true });
    }

    var nodes = textNodes(sr);
    var titles = state.items.map(function (it) { return norm(it.product_title); });

    state.items.forEach(function (item, idx) {
      var title = titles[idx];
      if (!title) return;

      nodes.forEach(function (tn) {
        if (norm(tn.nodeValue).indexOf(title) === -1) return;

        /* climb to the smallest ancestor that also holds a ₹ price → the line card */
        var el = tn.parentElement;
        var card = null;
        while (el && el !== sr.host) {
          var priceNodes = textNodes(el).filter(function (t) { return PRICE_RE.test(t.nodeValue) || unitOf.has(t); });
          if (priceNodes.length) { card = { el: el, prices: priceNodes }; break; }
          el = el.parentElement || (el.parentNode && el.parentNode.host) || null;
        }
        if (!card) return;

        /* must be this cart line: no other cart title inside, shows this quantity */
        var cardText = norm(card.el.textContent);
        for (var t = 0; t < titles.length; t++) {
          if (t !== idx && titles[t] && titles[t] !== title && cardText.indexOf(titles[t]) !== -1) return;
        }
        var qtyShown = Array.prototype.some.call(card.el.querySelectorAll('input'), function (i) { return String(i.value) === String(item.quantity); })
          || textNodes(card.el).some(function (x) { return x.nodeValue.trim() === String(item.quantity); });
        if (!qtyShown) return;

        card.prices.forEach(function (pn) {
          var info = unitOf.get(pn);
          var m = pn.nodeValue.match(PRICE_RE);
          /* the app re-rendered the unit price → take it as the new base */
          if (m && (!info || fmt(info.cents * info.qty, info.decimals) !== pn.nodeValue.trim())) {
            info = {
              cents: Math.round(parseFloat(m[1].replace(/,/g, '')) * 100),
              decimals: m[1].indexOf('.') !== -1,
              qty: 1
            };
          }
          if (!info) return;
          info.qty = item.quantity;
          unitOf.set(pn, info);
          var next = fmt(info.cents * item.quantity, info.decimals);
          if (pn.nodeValue.trim() !== next) pn.nodeValue = next;
        });
      });
    });
  }

  function paintSoon() {
    if (scheduled) return;
    scheduled = true;
    (window.requestAnimationFrame || setTimeout)(paint);
  }

  /* the drawer mounts late — watch for it */
  function watchHost() {
    if (document.getElementById('shiprocket-smart-cart-root')) { paintSoon(); }
    new MutationObserver(function () {
      var host = document.getElementById('shiprocket-smart-cart-root');
      if (host && host.shadowRoot && observed !== host.shadowRoot) paintSoon();
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchHost);
  else watchHost();
})();
